import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { EquipCategory } from "@/lib/equipment";

// ============================================================================
// EQUIP-03 (frontend) — AI classification for the long tail.
//
// The deterministic rules in lib/equipment.ts classify the known Lennox /
// Armstrong nomenclature. Whatever they leave as "other" gets sent here: we
// read the equipment_model_class cache, ask the classify-equipment-models
// Edge Function to label any still-uncached models (Gemini, cached forever),
// and return a Map keyed by "MFG|MODEL".
//
// Trust gate: only AI labels at confidence >= MIN_CONFIDENCE are applied; below
// that the unit stays "Other — needs review" (honest, awaiting human confirm —
// EQUIP-05). The result is a model->category dictionary Sedgwick owns.
// ============================================================================

export const MIN_CONFIDENCE = 0.6;

export interface ModelClass {
  category: EquipCategory;
  confidence: number | null;
  reason: string | null;
  source: "ai" | "human";
}

export interface ClassifyKey {
  mfg: string;
  model: string;
  count: number; // install frequency — used to prioritize which to classify first
}

export function modelKey(mfg: string | null, model: string | null): string {
  return `${(mfg ?? "").trim().toUpperCase()}|${(model ?? "").trim().toUpperCase()}`;
}

// Cap how many uncached models we send for AI classification per report load,
// most-frequent first — the Edge Function also caps, but trimming here keeps
// the request small and focuses spend on the high-volume tail.
const MAX_CLASSIFY = 200;

export function useModelClassifications(keys: ClassifyKey[]) {
  // Stable cache key from the distinct models requested (sorted), so React
  // Query dedupes and we don't re-invoke on every render.
  const distinct = Array.from(new Set(keys.map((k) => modelKey(k.mfg, k.model)))).sort();
  return useQuery({
    queryKey: ["model_classifications", distinct],
    enabled: distinct.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, ModelClass>> => {
      const result = new Map<string, ModelClass>();
      if (distinct.length === 0) return result;

      // 1. Read whatever is already cached (fast, no AI cost).
      const mfgs = Array.from(new Set(distinct.map((k) => k.split("|")[0])));
      const { data: cachedRows } = await supabase
        .from("equipment_model_class")
        .select("mfg_norm, model_norm, category, confidence, reason, source")
        .in("mfg_norm", mfgs);
      const wanted = new Set(distinct);
      for (const r of (cachedRows ?? []) as Array<{
        mfg_norm: string; model_norm: string; category: string;
        confidence: number | null; reason: string | null; source: string;
      }>) {
        const key = `${r.mfg_norm}|${r.model_norm}`;
        if (!wanted.has(key)) continue;
        result.set(key, {
          category: r.category as EquipCategory,
          confidence: r.confidence,
          reason: r.reason,
          source: (r.source as "ai" | "human") ?? "ai",
        });
      }

      // 2. Anything still missing → ask the Edge Function (most frequent first).
      const missing = keys
        .filter((k) => !result.has(modelKey(k.mfg, k.model)))
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_CLASSIFY);
      // De-dupe the missing set by key.
      const missingByKey = new Map<string, { mfg: string; model: string }>();
      for (const k of missing) {
        missingByKey.set(modelKey(k.mfg, k.model), { mfg: k.mfg, model: k.model });
      }
      if (missingByKey.size > 0) {
        const { data, error } = await supabase.functions.invoke("classify-equipment-models", {
          body: { items: Array.from(missingByKey.values()) },
        });
        if (!error && data?.classifications) {
          for (const [key, v] of Object.entries(data.classifications as Record<string, ModelClass>)) {
            result.set(key, v);
          }
        }
        // On error we simply return what we had — the report degrades to
        // "Other — needs review" for the tail rather than breaking.
      }

      return result;
    },
  });
}

// ----------------------------------------------------------------------------
// EQUIP-05 — read the full classification cache for a review/confirm panel, and
// let a user set a HUMAN category that overrides the AI label permanently.
// ----------------------------------------------------------------------------
export interface ModelClassRow {
  mfg_norm: string;
  model_norm: string;
  category: EquipCategory;
  confidence: number | null;
  reason: string | null;
  source: "ai" | "human";
}

export function useAllModelClassifications() {
  return useQuery({
    queryKey: ["all_model_classifications"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ModelClassRow[]> => {
      const { data, error } = await supabase
        .from("equipment_model_class")
        .select("mfg_norm, model_norm, category, confidence, reason, source")
        .order("confidence", { ascending: true, nullsFirst: true });
      if (error) throw error;
      return (data ?? []) as ModelClassRow[];
    },
  });
}

export function useSetHumanCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { mfg: string; model: string; category: EquipCategory }) => {
      const { data: auth } = await supabase.auth.getUser();
      const updated_by = auth?.user?.email ?? auth?.user?.id ?? null;
      const row = {
        mfg_norm: input.mfg.trim().toUpperCase(),
        model_norm: input.model.trim().toUpperCase(),
        category: input.category,
        confidence: 1,
        reason: "Confirmed by staff",
        source: "human" as const,
        updated_by,
        updated_at: new Date().toISOString(),
      };
      // Upsert on the PK; source='human' satisfies the EQUIP-05 RLS check and
      // wins over any prior AI row.
      const { error } = await supabase
        .from("equipment_model_class")
        .upsert(row, { onConflict: "mfg_norm,model_norm" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all_model_classifications"] });
      qc.invalidateQueries({ queryKey: ["model_classifications"] });
    },
  });
}
